/**
 * Shared empty-state surface for dark workspace lists and workspaces.
 */
import { Box, Button, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { GlassSurface } from "./GlassSurface";

export interface EmptyStateAction {
	label: string;
	/** Route link (preferred for navigation). */
	to?: string;
	/** Button click handler when not a route. */
	onClick?: () => void;
	variant?: "contained" | "outlined" | "text";
}

export interface EmptyStateProps {
	title: string;
	description: string;
	icon?: ReactNode;
	primaryAction?: EmptyStateAction;
	secondaryAction?: EmptyStateAction;
	/** Accessible section id for the title. */
	titleId?: string;
}

function ActionButton({ action }: { action: EmptyStateAction }) {
	const variant = action.variant ?? "contained";
	if (action.to) {
		return (
			<Button
				component={RouterLink}
				to={action.to}
				variant={variant}
			>
				{action.label}
			</Button>
		);
	}
	return (
		<Button variant={variant} onClick={action.onClick}>
			{action.label}
		</Button>
	);
}

export function EmptyState({
	title,
	description,
	icon,
	primaryAction,
	secondaryAction,
	titleId = "empty-state-title",
}: EmptyStateProps) {
	return (
		<GlassSurface
			component="section"
			aria-labelledby={titleId}
			intensity="strong"
			depth="raised"
			data-testid="empty-state"
			sx={{
				p: { xs: 3, sm: 5 },
				textAlign: "center",
				maxWidth: 560,
				mx: "auto",
			}}
		>
			{icon ? (
				<Box
					aria-hidden
					sx={{
						color: "workspace.textMuted",
						display: "flex",
						justifyContent: "center",
						mb: 1,
						"& .MuiSvgIcon-root": { fontSize: 48 },
					}}
				>
					{icon}
				</Box>
			) : null}
			<Typography
				id={titleId}
				component="h2"
				variant="h6"
				sx={{ fontWeight: 700 }}
			>
				{title}
			</Typography>
			<Typography
				variant="body2"
				sx={{
					color: "workspace.textSecondary",
					mt: 1,
					mb: 2.5,
					lineHeight: 1.55,
					maxWidth: 420,
					mx: "auto",
				}}
			>
				{description}
			</Typography>
			{(primaryAction || secondaryAction) && (
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={1.25}
					justifyContent="center"
					alignItems="center"
				>
					{primaryAction ? <ActionButton action={primaryAction} /> : null}
					{secondaryAction ? (
						<ActionButton
							action={{ ...secondaryAction, variant: secondaryAction.variant ?? "outlined" }}
						/>
					) : null}
				</Stack>
			)}
		</GlassSurface>
	);
}
